import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../config/axiosInstance';
import Snackbar from '../components/Snackbar';
import MenuButton from '../components/MenuButton';

// ─── Types ───────────────────────────────────────────────────────────────────

interface UserProfile {
    dateOfBirth?: string | null;
}

interface ApiUser {
    id: string;
    firstName: string;
    lastName: string;
    role: 'admin' | 'trainer' | 'user';
    userProfile?: UserProfile | null;
}

interface ApiRace {
    id: string;
    userId: string;
    athleteName: string;
    raceType: string;
    date: string; // YYYY-MM-DD
    location: string;
    status: string;
}

type CalendarEvent = {
    type: 'birthday' | 'race';
    label: string;
    subtitle?: string;
    /** Local date this event falls on for the currently-viewed month */
    date: Date;
};

const RACE_STATUSES = [
    'planned',
    'registered',
    'confirmed',
    'completed',
    'cancelled',
] as const;
type RaceStatus = (typeof RACE_STATUSES)[number];

/** Keep in sync with et-frontend/src/pages/Races/RaceModal.tsx */
const RACE_TYPES = [
    'Triathlon Sprint',
    'Triathlon Olympic',
    'Ironman 70.3',
    'Ironman',
    'Marathon',
    'Half Marathon',
    '10k Run',
    'Gran Fondo',
    'Ciclosportiva',
    'Altro',
];

const CUSTOM_TYPE = '__custom__';

interface NewEventForm {
    userIds: string[];
    raceType: string;
    date: string;
    location: string;
    status: RaceStatus;
    notes: string;
}

const EMPTY_FORM: NewEventForm = {
    userIds: [],
    raceType: '',
    date: '',
    location: '',
    status: 'planned',
    notes: '',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

/** Parse a YYYY-MM-DD string as a local date (avoids UTC off-by-one). */
const parseLocalDate = (value: string): Date | null => {
    if (!value) return null;
    const [y, m, d] = value.split('T')[0].split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

// ─── Component ───────────────────────────────────────────────────────────────

const Calendars: React.FC = () => {
    const { t, i18n } = useTranslation();

    const [users, setUsers] = useState<ApiUser[]>([]);
    const [races, setRaces] = useState<ApiRace[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        message: string;
        type: 'success' | 'error';
    } | null>(null);

    // Add Event modal
    const [isAddEventOpen, setIsAddEventOpen] = useState(false);
    const [form, setForm] = useState<NewEventForm>(EMPTY_FORM);
    const [athleteSearch, setAthleteSearch] = useState('');
    const [athleteDropdownOpen, setAthleteDropdownOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const athleteDropdownRef = useRef<HTMLDivElement>(null);

    // Race type: dropdown selection + free-text value when "custom" is chosen
    const [typeSelection, setTypeSelection] = useState('');
    const [customType, setCustomType] = useState('');
    const isCustomType = typeSelection === CUSTOM_TYPE;

    const today = useMemo(() => new Date(), []);
    const [viewDate, setViewDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });

    // ─── Data ────────────────────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [usersRes, racesRes] = await Promise.allSettled([
                axiosInstance.get('/users'),
                axiosInstance.get('/races/all'),
            ]);

            if (usersRes.status === 'fulfilled') {
                setUsers(usersRes.value.data || []);
            }
            if (racesRes.status === 'fulfilled') {
                setRaces(racesRes.value.data || []);
            }
            if (usersRes.status === 'rejected' && racesRes.status === 'rejected') {
                setError(t('calendars.loadError'));
            }
        } catch {
            setError(t('calendars.loadError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // ─── Add Event ───────────────────────────────────────────────────────────
    const athletes = useMemo(
        () =>
            users
                .filter((u) => u.role === 'user')
                .sort((a, b) =>
                    `${a.firstName} ${a.lastName}`.localeCompare(
                        `${b.firstName} ${b.lastName}`,
                    ),
                ),
        [users],
    );

    const openAddEvent = () => {
        setForm(EMPTY_FORM);
        setAthleteSearch('');
        setAthleteDropdownOpen(false);
        setTypeSelection('');
        setCustomType('');
        setIsAddEventOpen(true);
    };

    const handleTypeChange = (value: string) => {
        setTypeSelection(value);
        if (value === CUSTOM_TYPE) {
            setForm((f) => ({ ...f, raceType: customType }));
        } else {
            setForm((f) => ({ ...f, raceType: value }));
        }
    };

    const handleCustomTypeChange = (value: string) => {
        setCustomType(value);
        setForm((f) => ({ ...f, raceType: value }));
    };

    // Close the athlete dropdown when clicking outside it
    useEffect(() => {
        if (!athleteDropdownOpen) return;
        const onClick = (e: MouseEvent) => {
            if (
                athleteDropdownRef.current &&
                !athleteDropdownRef.current.contains(e.target as Node)
            ) {
                setAthleteDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [athleteDropdownOpen]);

    /** Athletes matching the in-modal search box */
    const visibleAthletes = useMemo(() => {
        const q = athleteSearch.trim().toLowerCase();
        if (!q) return athletes;
        return athletes.filter((a) =>
            `${a.firstName} ${a.lastName}`.toLowerCase().includes(q),
        );
    }, [athletes, athleteSearch]);

    const toggleAthlete = (id: string) =>
        setForm((f) => ({
            ...f,
            userIds: f.userIds.includes(id)
                ? f.userIds.filter((x) => x !== id)
                : [...f.userIds, id],
        }));

    /** Select-all applies to the currently visible (filtered) athletes. */
    const allVisibleSelected =
        visibleAthletes.length > 0 &&
        visibleAthletes.every((a) => form.userIds.includes(a.id));

    const selectAllVisible = () =>
        setForm((f) => ({
            ...f,
            userIds: Array.from(new Set([...f.userIds, ...visibleAthletes.map((a) => a.id)])),
        }));

    const clearAll = () => setForm((f) => ({ ...f, userIds: [] }));

    /** Text shown on the collapsed dropdown trigger. */
    const athleteSummary = useMemo(() => {
        if (form.userIds.length === 0) return t('calendars.selectAthletes');
        if (form.userIds.length === athletes.length && athletes.length > 0) {
            return t('calendars.allAthletes', { count: athletes.length });
        }
        if (form.userIds.length === 1) {
            const a = athletes.find((x) => x.id === form.userIds[0]);
            return a ? `${a.firstName} ${a.lastName}` : '';
        }
        return t('calendars.selectedCount', { count: form.userIds.length });
    }, [form.userIds, athletes, t]);

    const isFormValid =
        form.userIds.length > 0 &&
        !!form.raceType.trim() &&
        !!form.date &&
        !!form.location.trim();

    const handleCreateEvent = async () => {
        if (!isFormValid || saving) return;
        setSaving(true);
        try {
            const res = await axiosInstance.post('/races/bulk', {
                userIds: form.userIds,
                raceType: form.raceType.trim(),
                date: form.date,
                location: form.location.trim(),
                status: form.status,
                ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
            });
            const count = res.data?.created ?? form.userIds.length;
            setIsAddEventOpen(false);
            setForm(EMPTY_FORM);
            setSnackbar({
                message: t('calendars.eventCreated', { count }),
                type: 'success',
            });
            // Jump the calendar to the month of the new event, then refresh
            const created = parseLocalDate(form.date);
            if (created) {
                setViewDate(new Date(created.getFullYear(), created.getMonth(), 1));
            }
            await loadData();
        } catch {
            setSnackbar({ message: t('calendars.eventCreateError'), type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    // ─── Build events for the viewed month ─────────────────────────────────────
    const events = useMemo<CalendarEvent[]>(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const list: CalendarEvent[] = [];

        // Birthdays — recur yearly, so map DOB's month/day onto the viewed year
        users.forEach((u) => {
            const dobStr = u.userProfile?.dateOfBirth;
            if (!dobStr) return;
            const dob = parseLocalDate(dobStr);
            if (!dob) return;
            if (dob.getMonth() !== month) return;
            list.push({
                type: 'birthday',
                label: `${u.firstName} ${u.lastName}`.trim(),
                subtitle: t('calendars.birthday'),
                date: new Date(year, month, dob.getDate()),
            });
        });

        // Races — real dates
        races.forEach((r) => {
            const d = parseLocalDate(r.date);
            if (!d) return;
            if (d.getFullYear() !== year || d.getMonth() !== month) return;
            list.push({
                type: 'race',
                label: r.raceType || t('calendars.race'),
                subtitle: r.athleteName,
                date: d,
            });
        });

        return list;
    }, [users, races, viewDate, t]);

    const eventsForDay = (day: Date) => events.filter((e) => isSameDay(e.date, day));

    // ─── Build the month grid (leading/trailing days included) ─────────────────
    const grid = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstOfMonth = new Date(year, month, 1);
        const startWeekday = firstOfMonth.getDay(); // 0 = Sun
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysInPrevMonth = new Date(year, month, 0).getDate();

        const cells: { date: Date; inMonth: boolean }[] = [];

        // leading days from previous month
        for (let i = startWeekday - 1; i >= 0; i--) {
            cells.push({
                date: new Date(year, month - 1, daysInPrevMonth - i),
                inMonth: false,
            });
        }
        // current month
        for (let d = 1; d <= daysInMonth; d++) {
            cells.push({ date: new Date(year, month, d), inMonth: true });
        }
        // trailing days to complete the last week row
        while (cells.length % 7 !== 0) {
            const next = cells.length - (startWeekday + daysInMonth) + 1;
            cells.push({ date: new Date(year, month + 1, next), inMonth: false });
        }
        return cells;
    }, [viewDate]);

    // "This Week's Focus" — events from today through the next 7 days
    /**
     * Chronological list of the viewed month's events. At ~46px per column the
     * grid cells can only show icons, so mobile gets this readable list beneath
     * the grid instead. Not rendered at md+, where the cells carry labels.
     */
    const monthAgenda = useMemo<CalendarEvent[]>(
        () => [...events].sort((a, b) => a.date.getTime() - b.date.getTime()),
        [events],
    );

    const weekFocus = useMemo<CalendarEvent[]>(() => {
        const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const end = new Date(start);
        end.setDate(end.getDate() + 7);
        return events
            .filter((e) => e.date >= start && e.date <= end)
            .sort((a, b) => a.date.getTime() - b.date.getTime());
    }, [events, today]);

    // ─── Formatting ────────────────────────────────────────────────────────────
    const locale = i18n.language === 'it' ? 'it-IT' : 'en-US';
    const monthLabel = viewDate.toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric',
    });
    const weekdayLabels = useMemo(() => {
        const base = new Date(2024, 10, 3); // a Sunday
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(base);
            d.setDate(base.getDate() + i);
            return d.toLocaleDateString(locale, { weekday: 'short' });
        });
    }, [locale]);

    const goPrev = () =>
        setViewDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const goNext = () =>
        setViewDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    const goToday = () =>
        setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

    const formatFocusDate = (d: Date) =>
        d.toLocaleDateString(locale, { day: 'numeric', month: 'long' });

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="flex-1 flex flex-col min-w-0 bg-background relative overflow-hidden">
            {/* Header */}
            <header className="h-24 bg-primary border-b border-cyan-600 flex items-center justify-between px-4 md:px-8 shadow-md z-20 relative overflow-hidden flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-90"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                <div className="relative z-10 flex items-center justify-between gap-3 md:gap-6 w-full">
                    <div className="flex items-center gap-3 md:gap-4 min-w-0">
                        <MenuButton />
                        <div className="hidden sm:flex w-10 h-10 rounded-full bg-white/10 items-center justify-center text-white border border-white/20 flex-shrink-0">
                            <i className="fa-solid fa-calendar-check text-lg"></i>
                        </div>
                        <div className="min-w-0">
                            <h1 className="text-base md:text-xl font-bold text-white tracking-tight truncate">
                                {t('calendars.title')}
                            </h1>
                            {/* Subtitle wraps to three lines in the narrow mobile header. */}
                            <p className="hidden md:block text-cyan-100 text-xs font-medium">
                                {t('calendars.subtitle')}
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={openAddEvent}
                        aria-label={t('calendars.addEvent')}
                        className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-2 md:py-2.5 bg-white text-primary rounded font-bold hover:shadow-lg hover:bg-slate-50 transition-all shadow-md border-b-4 border-cyan-200 active:border-b-0 active:translate-y-1 flex-shrink-0"
                    >
                        <i className="fa-solid fa-plus text-sm md:text-lg"></i>
                        <span className="text-[11px] md:text-sm">{t('calendars.addEvent')}</span>
                    </button>
                </div>
            </header>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 scroll-smooth">
                <div className="space-y-4 md:space-y-6">
                    {/* Filter & Legend */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4 bg-white p-3 md:p-4 rounded-xl shadow-sm border border-slate-200">
                        <div className="flex items-center justify-between md:justify-start gap-2 md:gap-6">
                            <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1 md:flex-none">
                                <button
                                    onClick={goPrev}
                                    aria-label={t('calendars.prevMonth')}
                                    className="w-10 h-10 flex-shrink-0 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                                >
                                    <i className="fa-solid fa-chevron-left text-slate-400"></i>
                                </button>
                                {/* min-w only from md: at 375px a fixed 180px plus both
                                    chevrons and the Today button overflows the card. */}
                                <h2 className="text-base md:text-xl font-bold text-slate-800 capitalize md:min-w-[180px] text-center flex-1 md:flex-none truncate">
                                    {monthLabel}
                                </h2>
                                <button
                                    onClick={goNext}
                                    aria-label={t('calendars.nextMonth')}
                                    className="w-10 h-10 flex-shrink-0 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                                >
                                    <i className="fa-solid fa-chevron-right text-slate-400"></i>
                                </button>
                            </div>
                            <button
                                onClick={goToday}
                                className="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-primary transition-colors"
                            >
                                {t('calendars.today')}
                            </button>
                        </div>

                        <div className="flex items-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-red-400"></span>
                                <span className="text-slate-600 font-medium">
                                    {t('calendars.birthdays')}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-3 h-3 rounded-full bg-emerald-400"></span>
                                <span className="text-slate-600 font-medium">
                                    {t('calendars.races')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm font-medium">
                            {error}
                        </div>
                    )}

                    {/* Calendar grid */}
                    <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden relative">
                        {loading && (
                            <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                                <i className="fa-solid fa-circle-notch fa-spin text-primary text-2xl"></i>
                            </div>
                        )}

                        {/* Day headers */}
                        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                            {weekdayLabels.map((label, i) => (
                                <div
                                    key={i}
                                    className="py-3 text-center text-xs font-bold text-slate-400 uppercase tracking-wider"
                                >
                                    {label}
                                </div>
                            ))}
                        </div>

                        {/* Days */}
                        <div className="grid grid-cols-7 bg-slate-200 gap-px">
                            {grid.map(({ date, inMonth }, idx) => {
                                const dayEvents = eventsForDay(date);
                                const isToday = isSameDay(date, today);
                                return (
                                    <div
                                        key={idx}
                                        className={`min-h-[64px] md:min-h-[120px] p-1 md:p-2 transition-colors ${
                                            inMonth
                                                ? 'bg-white hover:bg-slate-50'
                                                : 'bg-slate-50 opacity-40'
                                        } ${isToday ? 'ring-2 ring-primary ring-inset bg-cyan-50' : ''}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span
                                                className={`text-sm font-semibold ${
                                                    isToday
                                                        ? 'text-primary font-bold'
                                                        : inMonth
                                                        ? 'text-slate-700'
                                                        : 'text-slate-400'
                                                }`}
                                            >
                                                {date.getDate()}
                                            </span>
                                        </div>

                                        {isToday && (
                                            <div className="mt-1 px-1 py-0.5 bg-primary/10 text-primary text-[9px] font-bold rounded text-center">
                                                {t('calendars.todayBadge')}
                                            </div>
                                        )}

                                        <div className="mt-1 space-y-1">
                                            {dayEvents.map((ev, i) => (
                                                <div
                                                    key={i}
                                                    title={
                                                        ev.subtitle
                                                            ? `${ev.label} — ${ev.subtitle}`
                                                            : ev.label
                                                    }
                                                    className={`text-[0.7rem] px-0.5 md:px-1.5 py-0.5 rounded truncate ${
                                                        ev.type === 'birthday'
                                                            ? 'bg-red-100 text-red-800 border-l-2 border-red-500'
                                                            : 'bg-emerald-50 text-emerald-800 border-l-2 border-emerald-500'
                                                    }`}
                                                >
                                                    <i
                                                        className={`fa-solid ${
                                                            ev.type === 'birthday'
                                                                ? 'fa-cake-candles'
                                                                : 'fa-flag-checkered'
                                                        } md:mr-1`}
                                                    ></i>
                                                    {/* At ~46px per column the label cannot fit, so
                                                        the chip is icon-only on mobile. */}
                                                    <span className="hidden md:inline">{ev.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Month agenda — mobile only. The grid above can only show
                        icons at this width, so the same events are listed here in
                        full. Hidden at md+, where the cells carry their labels. */}
                    <div className="md:hidden bg-white rounded-xl border border-slate-200 p-4">
                        <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2 capitalize">
                            <i className="fa-solid fa-list-ul text-primary"></i>
                            {monthLabel}
                        </h3>
                        {monthAgenda.length === 0 ? (
                            <p className="text-sm text-slate-400 py-4 text-center">
                                {t('calendars.noUpcoming')}
                            </p>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {monthAgenda.map((ev, i) => {
                                    const isToday = isSameDay(ev.date, today);
                                    return (
                                        <div key={i} className="flex items-center gap-3 py-2.5">
                                            <div
                                                className={`w-11 flex-shrink-0 text-center rounded-lg py-1 ${
                                                    isToday ? 'bg-primary text-white' : 'bg-slate-50 text-slate-600'
                                                }`}
                                            >
                                                <div className="text-sm font-bold leading-none">
                                                    {ev.date.getDate()}
                                                </div>
                                                <div className="text-[10px] uppercase tracking-wide mt-0.5">
                                                    {ev.date.toLocaleDateString(locale, { weekday: 'short' })}
                                                </div>
                                            </div>
                                            <div
                                                className={`w-1 h-8 rounded-full flex-shrink-0 ${
                                                    ev.type === 'birthday' ? 'bg-red-400' : 'bg-emerald-400'
                                                }`}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold text-slate-700 truncate">
                                                    {ev.label}
                                                </div>
                                                {ev.subtitle && (
                                                    <div className="text-xs text-slate-400 truncate">
                                                        {ev.subtitle}
                                                    </div>
                                                )}
                                            </div>
                                            <i
                                                className={`fa-solid ${
                                                    ev.type === 'birthday' ? 'fa-cake-candles text-red-400' : 'fa-flag-checkered text-emerald-500'
                                                } text-xs flex-shrink-0`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* This Week's Focus (view-only) */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4 md:p-6">
                        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                            <i className="fa-solid fa-list-check text-primary"></i>
                            {t('calendars.weekFocus')}
                        </h3>
                        {weekFocus.length === 0 ? (
                            <p className="text-sm text-slate-400 py-4 text-center">
                                {t('calendars.noUpcoming')}
                            </p>
                        ) : (
                            <div className="space-y-4">
                                {weekFocus.map((ev, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100"
                                    >
                                        <div
                                            className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                ev.type === 'birthday'
                                                    ? 'bg-red-100 text-red-600'
                                                    : 'bg-emerald-100 text-emerald-600'
                                            }`}
                                        >
                                            <i
                                                className={`fa-solid ${
                                                    ev.type === 'birthday'
                                                        ? 'fa-cake-candles'
                                                        : 'fa-flag-checkered'
                                                }`}
                                            ></i>
                                        </div>
                                        <div className="min-w-0">
                                            <h4 className="font-bold text-slate-700 truncate">
                                                {ev.type === 'birthday'
                                                    ? t('calendars.birthdayOf', { name: ev.label })
                                                    : ev.label}
                                            </h4>
                                            <p className="text-sm text-slate-500 truncate">
                                                {formatFocusDate(ev.date)}
                                                {ev.subtitle ? ` · ${ev.subtitle}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Event Modal */}
            {isAddEventOpen && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-slate-200 overflow-hidden max-h-[90dvh] flex flex-col">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-flag-checkered text-white text-lg"></i>
                                </div>
                                <h3 className="text-xl font-bold text-white">
                                    {t('calendars.addEventTitle')}
                                </h3>
                            </div>
                            <button
                                onClick={() => setIsAddEventOpen(false)}
                                className="w-8 h-8 rounded-lg hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4 md:p-6 space-y-3 md:space-y-4 bg-slate-50 overflow-y-auto">
                            <div className="p-3 rounded-lg bg-cyan-50 border border-cyan-200 text-xs text-cyan-800">
                                {t('calendars.addEventInfo')}
                            </div>

                            {/* Athletes (multi-select dropdown) */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('calendars.athletes')} <span className="text-cyan-500">*</span>
                                </label>

                                <div className="relative" ref={athleteDropdownRef}>
                                    {/* Trigger */}
                                    <button
                                        type="button"
                                        onClick={() => setAthleteDropdownOpen((o) => !o)}
                                        className="w-full flex items-center justify-between pl-10 pr-3 py-3 bg-white border border-cyan-200 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent relative"
                                    >
                                        <i className="fa-solid fa-users absolute left-3 top-3.5 text-slate-400"></i>
                                        <span
                                            className={`text-sm truncate ${
                                                form.userIds.length
                                                    ? 'text-slate-800 font-medium'
                                                    : 'text-slate-400'
                                            }`}
                                        >
                                            {athleteSummary}
                                        </span>
                                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                            {form.userIds.length > 0 && (
                                                <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-[10px] font-bold rounded-full">
                                                    {form.userIds.length}
                                                </span>
                                            )}
                                            <i
                                                className={`fa-solid fa-chevron-down text-slate-400 text-xs transition-transform ${
                                                    athleteDropdownOpen ? 'rotate-180' : ''
                                                }`}
                                            ></i>
                                        </div>
                                    </button>

                                    {/* Dropdown panel */}
                                    {athleteDropdownOpen && (
                                        <div className="absolute z-20 mt-1 w-full bg-white border border-cyan-200 rounded-lg shadow-xl overflow-hidden">
                                            {/* Search */}
                                            <div className="p-2 border-b border-slate-100">
                                                <div className="relative">
                                                    <i className="fa-solid fa-magnifying-glass absolute left-3 top-2.5 text-slate-400 text-xs"></i>
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        value={athleteSearch}
                                                        onChange={(e) => setAthleteSearch(e.target.value)}
                                                        className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                                        placeholder={t('calendars.searchAthletes')}
                                                    />
                                                </div>
                                            </div>

                                            {/* Select all / Clear all */}
                                            <div className="flex items-center gap-2 px-2 py-2 border-b border-slate-100 bg-slate-50">
                                                <button
                                                    type="button"
                                                    onClick={selectAllVisible}
                                                    disabled={
                                                        visibleAthletes.length === 0 || allVisibleSelected
                                                    }
                                                    className="flex-1 px-3 py-1.5 text-xs font-bold text-cyan-700 bg-white border border-cyan-200 rounded-md hover:bg-cyan-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    <i className="fa-solid fa-check-double mr-1.5"></i>
                                                    {t('calendars.selectAll')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={clearAll}
                                                    disabled={form.userIds.length === 0}
                                                    className="flex-1 px-3 py-1.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    <i className="fa-solid fa-xmark mr-1.5"></i>
                                                    {t('calendars.clearAll')}
                                                </button>
                                            </div>

                                            {/* Checkbox list */}
                                            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                                                {visibleAthletes.length === 0 ? (
                                                    <p className="text-xs text-slate-400 text-center py-6">
                                                        {t('calendars.noAthletes')}
                                                    </p>
                                                ) : (
                                                    visibleAthletes.map((a) => {
                                                        const checked = form.userIds.includes(a.id);
                                                        return (
                                                            <label
                                                                key={a.id}
                                                                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                                                                    checked
                                                                        ? 'bg-cyan-50'
                                                                        : 'hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={checked}
                                                                    onChange={() => toggleAthlete(a.id)}
                                                                    className="w-4 h-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer"
                                                                />
                                                                <span
                                                                    className={`text-sm ${
                                                                        checked
                                                                            ? 'text-cyan-900 font-semibold'
                                                                            : 'text-slate-700'
                                                                    }`}
                                                                >
                                                                    {a.firstName} {a.lastName}
                                                                </span>
                                                            </label>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Race Type */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('calendars.raceType')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-flag-checkered absolute left-3 top-3.5 text-slate-400"></i>
                                    <select
                                        value={typeSelection}
                                        onChange={(e) => handleTypeChange(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent appearance-none"
                                    >
                                        <option value="">{t('calendars.selectRaceType')}</option>
                                        {RACE_TYPES.map((rt) => (
                                            <option key={rt} value={rt}>
                                                {rt}
                                            </option>
                                        ))}
                                        <option value={CUSTOM_TYPE}>
                                            {t('calendars.customRaceType')}
                                        </option>
                                    </select>
                                </div>

                                {isCustomType && (
                                    <div className="relative">
                                        <i className="fa-solid fa-pen absolute left-3 top-3.5 text-slate-400"></i>
                                        <input
                                            type="text"
                                            autoFocus
                                            value={customType}
                                            onChange={(e) => handleCustomTypeChange(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                            placeholder={t('calendars.raceTypePlaceholder')}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Date */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('calendars.date')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-calendar absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="date"
                                        value={form.date}
                                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            {/* Location */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('calendars.location')} <span className="text-cyan-500">*</span>
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-location-dot absolute left-3 top-3.5 text-slate-400"></i>
                                    <input
                                        type="text"
                                        value={form.location}
                                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                        placeholder={t('calendars.locationPlaceholder')}
                                    />
                                </div>
                            </div>

                            {/* Status */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('calendars.status')}
                                </label>
                                <div className="relative">
                                    <i className="fa-solid fa-circle-check absolute left-3 top-3.5 text-slate-400"></i>
                                    <select
                                        value={form.status}
                                        onChange={(e) =>
                                            setForm({ ...form, status: e.target.value as RaceStatus })
                                        }
                                        className="w-full pl-10 pr-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent appearance-none"
                                    >
                                        {RACE_STATUSES.map((s) => (
                                            <option key={s} value={s}>
                                                {t(`calendars.statuses.${s}`)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('calendars.notes')}
                                </label>
                                <textarea
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    rows={2}
                                    className="w-full px-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                                    placeholder={t('calendars.notesPlaceholder')}
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-4 md:px-6 py-3 md:py-4 bg-white border-t border-slate-200 flex gap-2 md:gap-3 flex-shrink-0">
                            <button
                                onClick={() => setIsAddEventOpen(false)}
                                className="px-4 py-2.5 md:flex-1 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded-lg font-medium transition-colors border border-cyan-200 whitespace-nowrap"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleCreateEvent}
                                disabled={!isFormValid || saving}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                                {saving ? (
                                    <i className="fa-solid fa-circle-notch fa-spin mr-2"></i>
                                ) : (
                                    <i className="fa-solid fa-plus mr-2"></i>
                                )}
                                {t('calendars.scheduleEvent')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}
        </div>
    );
};

export default Calendars;
